import React, { useState } from "react";
import { ArrowRight } from "lucide-react";

import PageLayout from "@/components/layout/PageLayout";
import Seo from "@/components/foundation/Seo";
import Container from "@/components/foundation/Container";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import Badge from "@/components/foundation/Badge";
import Button from "@/components/foundation/Button";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import GridPattern from "@/components/foundation/GridPattern";

import PhoneMockup from "@/components/marketing/PhoneMockup";
import DashboardScreen from "@/components/marketing/AppScreens/DashboardScreen";
import MerchantWaitlist from "@/components/marketing/MerchantWaitlist";
import ComingSoonModal from "@/components/marketing/ComingSoonModal";
import Timeline from "@/components/marketing/Timeline";
import PartnerLink, { AutoLinkPartners } from "@/components/marketing/PartnerLink";

import {
  Brain,
  Waves,
  ShieldCheck,
  Cable,
  Lock,
  KeyRound,
  FolderLock,
  Gauge,
  BarChart3,
  LayoutDashboard,
  FileLock2,
  ClipboardCheck,
  RadioTower,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

/* Reused merchant benefits (top block) */
const BENEFITS = [
  {
    icon: Brain,
    title: "AI-Powered Funding Insights",
    body: "Understand your funding potential today, not weeks from now. ZenScore-driven insights explain exactly where you stand and what to improve.",
  },
  {
    icon: Waves,
    title: "Real Cash-Flow Intelligence",
    body: "Live cash flow, deposit patterns, and balance trends analyzed continuously — so your funding readiness reflects how your business is actually performing.",
  },
  {
    icon: ShieldCheck,
    title: "No Impact on Personal Credit",
    body: "Bank connections are read-only via Mastercard Finicity. Nothing about connecting your bank triggers a credit inquiry or affects your credit profile.",
  },
];

const STEPS = [
  { label: "Join the Waitlist", description: "Reserve early access and set up your business profile the moment we're live." },
  { label: "Connect Your Bank", description: "Secure read-only link through Mastercard Finicity." },
  { label: "AI Analysis", description: "Cash flow, deposits, and balances analyzed continuously." },
  { label: "Get Your ZenScore", description: "See your composite score and full category breakdown." },
  { label: "Apply for Funding", description: "Streamlined application pre-filled from verified banking data." },
  { label: "Match with Partners", description: "Qualified profiles are matched to relevant funding opportunities." },
];

const FEATURES = [
  { icon: Brain, title: "AI-Powered ZenScore", body: "Live funding readiness intelligence." },
  { icon: Gauge, title: "Funding Readiness Insights", body: "Know where you stand and what improves next." },
  { icon: BarChart3, title: "Revenue Analytics", body: "Growth velocity, channel signal, seasonality." },
  { icon: Waves, title: "Cash Flow Trends", body: "Inflow/outflow trajectories + stability scoring." },
  { icon: LayoutDashboard, title: "Funding Dashboard", body: "Applications, offers, and matches in one view." },
  { icon: FileLock2, title: "Secure Document Storage", body: "Encrypted vault for statements + tax records." },
  { icon: ClipboardCheck, title: "Simple Application", body: "Pre-filled forms — minimal typing required." },
  { icon: RadioTower, title: "Real-Time Updates", body: "Live status changes for applications & offers." },
  { icon: Sparkles, title: "Personalized Funding Offers", body: "Offers tailored to your ZenScore and profile." },
];

const QUALIFY = [
  "US-based business",
  "Active business bank account (Mastercard Finicity-supported)",
  "Consistent monthly deposits",
  "At least a few months of operating history",
  "Business owner willing to authorize secure banking data access",
];

const TRUST_ICONS = [
  { icon: Cable, label: "Mastercard Finicity", partner: "finicity", sub: "Bank connectivity" },
  { icon: Lock, label: "256-bit Encryption", sub: "Data at rest & in transit" },
  { icon: FolderLock, label: "Secure Vault", sub: "Per-tenant encrypted docs" },
  { icon: KeyRound, label: "Credentials Never Stored", sub: "Read-only connections" },
];

export default function Businesses() {
  const [modal, setModal] = useState({ open: false, source: "businesses_hero_waitlist" });
  const openModal = (source) => setModal({ open: true, source });
  const setOpen = (open) => setModal((s) => ({ ...s, open }));

  return (
    <PageLayout>
      <Seo
        title="ZenMe Capital · For Businesses"
        description="Understand your business funding potential in minutes. Connect your bank via Mastercard Finicity, receive your ZenScore, and unlock personalized funding opportunities."
        path="/businesses"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-24"
        data-testid="businesses-hero"
      >
        <GridPattern variant="dots" size={28} opacity={0.4} />
        <GlowOrb color="emerald" size={520} intensity={0.2} className="-bottom-40 -left-40" />

        <Container className="relative z-10">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <FadeIn>
                <Badge variant="emerald" pulse>For Businesses</Badge>
              </FadeIn>
              <FadeIn delay={0.06}>
                <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
                  Understand your funding potential in{" "}
                  <span className="text-brand-emerald">minutes.</span>
                </h1>
              </FadeIn>
              <FadeIn delay={0.14}>
                <p className="mt-6 max-w-xl text-base text-text-secondary sm:text-lg leading-relaxed">
                  Securely connect your bank, receive your ZenScore, and unlock
                  personalized funding opportunities — all from one intelligent
                  platform.
                </p>
              </FadeIn>
              <FadeIn delay={0.22}>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => openModal("businesses_hero_waitlist")}
                    rightIcon={<ArrowRight className="h-4 w-4" />}
                    data-testid="businesses-waitlist-btn"
                  >
                    Join the Waitlist
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => (window.location.href = "/zenscore")}
                  >
                    Learn about ZenScore
                  </Button>
                </div>
              </FadeIn>
            </div>

            <div className="flex items-center justify-center lg:col-span-5 lg:justify-end">
              <FadeIn delay={0.2} direction="left">
                <PhoneMockup width={320}>
                  <DashboardScreen />
                </PhoneMockup>
              </FadeIn>
            </div>
          </div>
        </Container>
      </section>

      {/* WHAT YOU GET */}
      <Section
        eyebrow="What You Get"
        headline="Three pillars, one platform."
        subheadline="ZenMe replaces documents, phone calls, and long waits with continuous financial intelligence."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <FadeIn key={b.title} delay={0.06 * i}>
                <Card variant="elevated" className="flex h-full flex-col gap-4 p-8">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-brand-emerald/25 bg-brand-emerald/10 text-brand-emerald">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">
                      {b.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                      <AutoLinkPartners text={b.body} />
                    </p>
                  </div>
                </Card>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section
        eyebrow="How It Works"
        headline="From bank connection to funding, in six steps."
        subheadline="A streamlined path from onboarding to matched funding opportunities."
      >
        <Timeline steps={STEPS} />
      </Section>

      {/* FEATURE GRID */}
      <Section
        eyebrow="Full Feature Set"
        headline="Everything you need to understand your funding potential."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <FadeIn key={f.title} delay={0.04 * i}>
                <Card variant="elevated" className="flex h-full flex-col p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-brand-emerald/25 bg-brand-emerald/8 text-brand-emerald">
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  </div>
                  <h4 className="mt-4 font-display text-base font-semibold text-white">
                    {f.title}
                  </h4>
                  <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                    {f.body}
                  </p>
                </Card>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* WHO QUALIFIES */}
      <Section
        eyebrow="Who Qualifies"
        headline="Businesses of all sizes with active revenue may qualify."
        subheadline="Every profile is reviewed individually. There are no fixed credit-score thresholds — ZenScore evaluates real business activity."
      >
        <FadeIn>
          <Card variant="glass" interactive={false} className="mx-auto max-w-3xl p-8 sm:p-10">
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {QUALIFY.map((q) => (
                <li key={q} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-emerald text-black">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm text-white">
                    <AutoLinkPartners text={q} />
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs italic text-text-muted">
              ZenMe does not guarantee funding availability. Final eligibility
              is determined by the individual funding partner and product.
            </p>
          </Card>
        </FadeIn>
      </Section>

      {/* SECURITY BAND */}
      <section className="relative border-y border-white/[0.05] bg-bg-raised py-16">
        <Container>
          <FadeIn>
            <p className="text-center font-mono text-[11px] uppercase tracking-[0.24em] text-text-muted">
              Security & Trust
            </p>
          </FadeIn>
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {TRUST_ICONS.map((t, i) => {
              const Icon = t.icon;
              return (
                <FadeIn key={t.label} delay={0.05 * i}>
                  <Card variant="flat" className="flex h-full items-center gap-3 p-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-brand-emerald/25 bg-brand-emerald/8 text-brand-emerald">
                      <Icon className="h-4 w-4" strokeWidth={1.75} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-white leading-tight">
                        {t.partner ? (
                          <PartnerLink partner={t.partner} showGlyph={false} className="!text-white hover:!text-brand-emerald">
                            {t.label}
                          </PartnerLink>
                        ) : (
                          t.label
                        )}
                      </span>
                      <span className="text-xs text-text-secondary">
                        {t.sub}
                      </span>
                    </div>
                  </Card>
                </FadeIn>
              );
            })}
          </div>
        </Container>
      </section>

      {/* WAITLIST BAND */}
      <section
        className="relative overflow-hidden py-24 sm:py-32"
        data-testid="businesses-waitlist-band"
      >
        <GlowOrb color="emerald" size={520} intensity={0.2} className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        <Container className="relative z-10">
          <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 text-center">
            <FadeIn>
              <h2 className="font-display text-4xl font-semibold text-white sm:text-5xl">
                Be first when the app launches.
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-base text-text-secondary sm:text-lg">
                Drop your business email and we&rsquo;ll send you early access
                as soon as we&rsquo;re live.
              </p>
            </FadeIn>
            <FadeIn delay={0.18} className="w-full">
              <MerchantWaitlist
                size="lg"
                source="businesses_waitlist"
                data-testid="businesses-waitlist"
              />
            </FadeIn>
          </div>
        </Container>
      </section>

      <ComingSoonModal open={modal.open} onOpenChange={setOpen} source={modal.source} />
    </PageLayout>
  );
}
